import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../lib/api-client';

/**
 * Loading, error, empty and offline states.
 *
 * Written as first-class components rather than inline ternaries, because a
 * state that has its own file gets designed. These are the screens people
 * actually see on a bad connection, and on a brand site they have to look
 * deliberate rather than like a failure that leaked through.
 */

export function Skeleton({
  className = '',
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return <div className={`skeleton ${className}`} style={style} aria-hidden="true" />;
}

/** Grid placeholder that reserves the real layout, so nothing shifts on load. */
export function ProductGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="product-grid" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div key={index}>
          <Skeleton className="skeleton--card" />
          <div className="mt-sm stack">
            <Skeleton className="skeleton--text" />
            <Skeleton className="skeleton--text" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Screen-reader announcement for an in-progress load.
 *
 * The skeleton is `aria-hidden`, so without this a screen-reader user gets
 * silence while the page appears to do nothing.
 */
export function LoadingAnnouncer({ label = 'Loading' }: { label?: string }) {
  return (
    <p role="status" aria-live="polite" className="visually-hidden">
      {label}
    </p>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="state">
      <p className="label">Nothing here</p>
      <h2 className="state__title">{title}</h2>
      {body ? <p className="state__body">{body}</p> : null}
      {action}
    </div>
  );
}

/**
 * Error state.
 *
 * Distinguishes three cases, because they need different words: an unreachable
 * network (the customer can act - retry), an endpoint that does not exist yet
 * (nobody can act - say so plainly rather than blaming the connection), and
 * everything else. The request id is surfaced when the API sends one, so a
 * support message can be traced to a log line.
 */
export function ErrorState({
  error,
  onRetry,
  title,
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}) {
  const apiError = error instanceof ApiError ? error : null;
  const isOffline = apiError?.status === 0;
  const isMissingEndpoint = apiError?.isUnimplemented ?? false;

  const heading =
    title ?? (isOffline ? 'No connection' : isMissingEndpoint ? 'Not available yet' : 'Something broke');

  const body = isOffline
    ? 'We could not reach the server. Check your connection and try again.'
    : isMissingEndpoint
      ? 'This part of the store is not live yet. Everything else still works.'
      : (apiError?.message ?? 'An unexpected error occurred.');

  return (
    <div className="state" role="alert">
      <p className="label">Error</p>
      <h2 className="state__title">{heading}</h2>
      <p className="state__body">{body}</p>
      {apiError?.requestId ? (
        <p className="label faint">Reference {apiError.requestId}</p>
      ) : null}
      <div className="mt-sm" style={{ display: 'flex', gap: '0.75rem' }}>
        {onRetry && !isMissingEndpoint ? (
          <button type="button" className="btn btn--sm" onClick={onRetry}>
            Try again
          </button>
        ) : null}
        <Link to="/shop" className="btn btn--sm">
          Continue shopping
        </Link>
      </div>
    </div>
  );
}

/**
 * Offline banner.
 *
 * A sticky bar rather than a modal: losing connection should not lock someone
 * out of a page they can still read. Browsing already-cached products keeps
 * working; only writes will fail, and they fail with their own message.
 */
export function OfflineBanner({ online }: { online: boolean }) {
  if (online) return null;
  return (
    <div className="offline-bar" role="status" aria-live="polite">
      Offline - you can keep browsing, but orders will not go through
    </div>
  );
}
